import { Fragment, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useViewport } from "src/contexts";
import { GoToStep, useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { createConfiguratorUrl } from "src/RouteUrls";
import {
  buildSummaryListEffectiveLineItems,
  filterSelectedOptionsBySteps,
  filterSummaryEffectiveOptionGroups,
  getSummaryGroupsEffectivePriceInCents,
  getSummaryPlanDescription,
} from "src/utils/summary.utils";
import { AccordionOptionCard, IconTooltip } from "~components";
import { Css, Palette } from "~generated/css";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import {
  currencyFormatter,
  formatDelta,
  formatDeltaPriceDisplay,
  getProgramDataStrings,
  isDefined,
  useTestIds,
} from "~utils";
import { FlowStep } from "../stepTemplates/FlowSteps";
import { SummaryCards_CheckoutConfigFragment } from "./SummaryCards.generated";
type SummaryCardListProps = {
  contrast?: boolean;
  /**
   * Toggles the visibility of the `Plan Cost` and `Est. Total Cost` card.
   * This is used for the `/hop-selections` page
   * @default false
   */
  hideTotals?: boolean;
  checkoutConfig: SummaryCards_CheckoutConfigFragment;
} & GoToStep;

export function SummaryCardList(props: SummaryCardListProps) {
  const { checkoutConfig, contrast = false, goToStep, hideTotals = false } = props;
  const tid = useTestIds(props, "summary-card-list");
  const { hasAduSupport } = useConfiguratorContext();

  return (
    <>
      {hideTotals ? null : <BuildCostCard checkoutConfig={checkoutConfig} contrast={contrast} {...tid.buildCostCard} />}
      <ExteriorCard checkoutConfig={checkoutConfig} goToStep={goToStep} contrast={contrast} {...tid.planExteriorCard} />
      <InteriorPersonalizationCard
        checkoutConfig={checkoutConfig}
        goToStep={goToStep}
        contrast={contrast}
        {...tid.interiorPersonalizationCard}
      />
      <AddOnsCard checkoutConfig={checkoutConfig} goToStep={goToStep} contrast={contrast} {...tid.addOnsCard} />
      {hasAduSupport && (
        <AduCard checkoutConfig={checkoutConfig} goToStep={goToStep} contrast={contrast} {...tid.aduCard} />
      )}
    </>
  );
}

type SummaryCardBaseProps = {
  checkoutConfig: SummaryCards_CheckoutConfigFragment;
  contrast: boolean;
} & GoToStep;

type PlanExteriorCardProps = SummaryCardBaseProps;

export function ExteriorCard(props: PlanExteriorCardProps) {
  const { contrast = false, goToStep, checkoutConfig } = props;
  const { reservedAt, optionGroups } = checkoutConfig;
  const tid = useTestIds(props, "plan-exterior-card");

  const titleAction =
    reservedAt === null ? { triggerText: "Edit", onClick: () => goToStep(FlowStep.Exterior) } : undefined;

  const { planName, planProgramData } = getSummaryPlanDescription(checkoutConfig);

  const exteriorOptions = filterSelectedOptionsBySteps(optionGroups, [
    CheckoutConfigOptionGroupStep.ExteriorElevation,
    CheckoutConfigOptionGroupStep.ExteriorDesignPackage,
  ]);

  // Note: CheckoutConfigOptionGroupStep.OptionsStructural is not a required OG
  const floorPlanOptionGroups = filterSummaryEffectiveOptionGroups(optionGroups, [
    CheckoutConfigOptionGroupStep.OptionsStructural,
  ]);

  return (
    <AccordionOptionCard
      contrast={contrast}
      title="Plan & Exterior"
      titleAction={titleAction}
      children={
        <div css={Css.df.fdc.$}>
          <SummaryListItem
            contrast={contrast}
            title="Floor Plan"
            priceInCents={checkoutConfig.salesPriceInCents}
            items={[{ title: planName }, { title: planProgramData }]}
          />
          <SummaryListItem
            contrast={contrast}
            title="Exterior"
            priceInCents={exteriorOptions.sum((cco) => cco.totalPriceInCents)}
            items={exteriorOptions.map((cco) => ({ title: cco.option.name, priceInCents: cco.totalPriceInCents }))}
          />
          <SummaryListItem
            title="Floor Plan Options"
            contrast={contrast}
            priceInCents={getSummaryGroupsEffectivePriceInCents(floorPlanOptionGroups)}
            items={buildSummaryListEffectiveLineItems(floorPlanOptionGroups)}
            last
          />
        </div>
      }
      {...tid.accordionOptionCard}
    />
  );
}

function InteriorPersonalizationCard(props: SummaryCardBaseProps) {
  const { checkoutConfig, contrast = false } = props;
  const { reservedAt } = checkoutConfig;

  const tid = useTestIds(props, "interior-personalization-card");
  const navigate = useNavigate();

  const { communityId } = useConfiguratorContext();

  const interiorSubSteps = checkoutConfig.interiorSubSteps ?? [];

  // TODO: Confirm if we can assume there will always be at least one selection?
  const personalizationGroupWithSelections = checkoutConfig.optionGroups
    .filter((ccog) => !!ccog.subStepNumber && !!ccog.selectedOption)
    .groupBy((ccog) => ccog.subStepNumber!);

  const titleAction = useCallback(
    (subStepNumber: number) => {
      if (reservedAt) return undefined;

      return {
        triggerText: "Edit",
        onClick: () =>
          navigate(createConfiguratorUrl(communityId!, checkoutConfig!.id!, FlowStep.Interior, `${subStepNumber}`)),
      };
    },
    [reservedAt, communityId, checkoutConfig, navigate],
  );

  return (
    <>
      {interiorSubSteps.map(({ displayName, stepNumber }) => {
        return (
          <AccordionOptionCard
            key={displayName}
            contrast={contrast}
            title={displayName}
            titleAction={titleAction(stepNumber)}
            children={
              <div css={Css.df.fdc.$}>
                {personalizationGroupWithSelections[stepNumber]?.map((optionGroup) => {
                  return (
                    <SummaryListItem
                      key={optionGroup.id}
                      contrast={contrast}
                      title={optionGroup.group.name}
                      priceInCents={optionGroup.selectedOption?.totalPriceInCents ?? 0}
                      items={[
                        {
                          title: optionGroup.selectedOption!.option.name,
                          priceInCents: optionGroup.selectedOption?.totalPriceInCents,
                        },
                      ]}
                      last
                    />
                  );
                })}
              </div>
            }
            {...tid.accordionOptionCard}
          />
        );
      })}
    </>
  );
}

export function AddOnsCard(props: SummaryCardBaseProps) {
  const { goToStep, checkoutConfig, contrast = false } = props;
  const { optionGroups, reservedAt } = checkoutConfig;
  const tid = useTestIds(props, "add-ons-card");
  const titleAction =
    reservedAt === null ? { triggerText: "Edit", onClick: () => goToStep(FlowStep.Options) } : undefined;
  const selectedAddOns = filterSummaryEffectiveOptionGroups(optionGroups, [CheckoutConfigOptionGroupStep.OptionsAddon]);

  return (
    <AccordionOptionCard
      contrast={contrast}
      title="Add-Ons"
      titleAction={titleAction}
      children={
        <div css={Css.df.fdc.$}>
          <SummaryListItem
            contrast={contrast}
            title="Add-Ons"
            priceInCents={getSummaryGroupsEffectivePriceInCents(selectedAddOns)}
            items={buildSummaryListEffectiveLineItems(selectedAddOns, true)}
            last
          />
        </div>
      }
      {...tid.accordionOptionCard}
    />
  );
}

export function AduCard(props: SummaryCardBaseProps) {
  const { goToStep, checkoutConfig, contrast = false } = props;
  const { optionGroups, reservedAt } = checkoutConfig;
  const tid = useTestIds(props, "adu-card");
  const titleAction = reservedAt === null ? { triggerText: "Edit", onClick: () => goToStep(FlowStep.Adu) } : undefined;
  const selectedAduOptions = filterSummaryEffectiveOptionGroups(optionGroups, [CheckoutConfigOptionGroupStep.Adu]);

  // Legacy ADU — just show all selections in a flat list
  if (!checkoutConfig.aduProductOffering) {
    return (
      <AccordionOptionCard
        contrast={contrast}
        title="ADU"
        titleAction={titleAction}
        children={
          <div css={Css.df.fdc.$}>
            <SummaryListItem
              contrast={contrast}
              title="ADU Selections"
              priceInCents={getSummaryGroupsEffectivePriceInCents(selectedAduOptions)}
              items={buildSummaryListEffectiveLineItems(selectedAduOptions, true)}
              last
            />
          </div>
        }
        {...tid.accordionOptionCard}
      />
    );
  }

  const { bedrooms, bathrooms, sqft } = getProgramDataStrings(checkoutConfig.aduProgramData);
  const { name } = checkoutConfig.aduProductOffering;
  const aduSalesPrice = checkoutConfig.aduSalesPriceInCents;

  const aduOptionsByGroup = selectedAduOptions.groupBy((ccog) =>
    ccog.group.group.type.isInteriorScheme
      ? "Interior Scheme"
      : ccog.group.group.type.isSpecLevel
        ? "Spec Level"
        : "ADU Selections",
  );

  return (
    <AccordionOptionCard
      contrast={contrast}
      title="ADU"
      titleAction={titleAction}
      children={
        <div css={Css.df.fdc.$}>
          <SummaryListItem
            contrast={contrast}
            title="Floor Plan"
            priceInCents={aduSalesPrice}
            items={[{ title: name, priceInCents: aduSalesPrice }, { title: `${bedrooms}, ${bathrooms} & ${sqft}` }]}
          />
          {Object.entries(aduOptionsByGroup).map(([groupName, optionGroups]) => (
            <SummaryListItem
              key={groupName}
              contrast={contrast}
              title={groupName}
              priceInCents={getSummaryGroupsEffectivePriceInCents(optionGroups)}
              items={buildSummaryListEffectiveLineItems(optionGroups, true)}
            />
          ))}
        </div>
      }
      {...tid.accordionOptionCard}
    />
  );
}

type BuildCostCardProps = Pick<SummaryCardBaseProps, "contrast" | "checkoutConfig">;

export function BuildCostCard(props: BuildCostCardProps) {
  const { checkoutConfig, contrast = false } = props;
  const { isMobile } = useViewport();
  const tid = useTestIds(props, "build-cost-card");
  const hasAdu = !!checkoutConfig.aduProductOffering;
  return (
    <div css={Css.p2.borderRadius("0 0 4px 4px").relative.$}>
      <div css={Css.aic.df.jcsb.if(hasAdu).mb2.$}>
        <h6 css={Css.df.t16.fw300.lhPx(28).gapPx(4).if(contrast).white.$}>
          Estimated Total Cost
          <div>
            <IconTooltip
              type="info"
              message="Price shown is an estimate for your build based on the selections detailed above. A Homebound representative will confirm a final total cost before contracting."
              {...tid.infoTooltipTotalCost}
              contrast={contrast}
              place={isMobile ? "bottom" : undefined}
            />
          </div>
        </h6>
        <h6 css={Css.t16.fw300.lhPx(28).gap1.if(contrast).white.$}>
          {currencyFormatter(checkoutConfig.totalPriceInCents)}
        </h6>
      </div>
      {hasAdu && (
        <>
          <div css={Css.aic.df.jcsb.pt2.mb2.bt.bcGray200.if(contrast).bc("rgba(252, 252, 250, 0.10)").$}>
            <h6 css={Css.df.t16.fw300.lhPx(28).gapPx(4).if(contrast).white.$}>
              Main Plan
              <div>
                <IconTooltip
                  type="info"
                  message="Cost to build your home including any fixed-cost options selected. In addition, your build will include site-specific costs, which are included in the total cost estimate below. Site-specific costs will be confirmed prior to signing your contract."
                  {...tid.infoTooltipPlanCost}
                  contrast={contrast}
                  place={isMobile ? "bottom" : undefined}
                />
              </div>
            </h6>
            <h6 css={Css.t16.fw300.lhPx(28).gap1.if(contrast).white.$}>
              {currencyFormatter(checkoutConfig.mhTotalPriceInCents)}
            </h6>
          </div>
          <div css={Css.aic.df.jcsb.pt2.bt.bcGray200.if(contrast).bc("rgba(252, 252, 250, 0.10)").$}>
            <h6 css={Css.df.t16.fw300.lhPx(28).gapPx(4).if(contrast).white.$}>
              ADU Plan
              <div>
                {/* TODO: Tooltip copy is being reviewed by legal */}
                <IconTooltip
                  type="info"
                  message="Cost to build your ADU including any selected options."
                  {...tid.infoTooltipAduPlanCost}
                  contrast={contrast}
                  place={isMobile ? "bottom" : undefined}
                />
              </div>
            </h6>
            <h6 css={Css.t16.fw300.lhPx(28).gap1.if(contrast).white.$}>
              {currencyFormatter(checkoutConfig.aduTotalPriceInCents)}
            </h6>
          </div>
        </>
      )}
    </div>
  );
}

type SummaryListItemProps = {
  title?: string;
  priceInCents: number;
  items: { title: string; priceInCents?: number }[];
  contrast?: boolean; // TODO: usecontrast?
  last?: boolean;
};

export function SummaryListItem(props: SummaryListItemProps) {
  const tid = useTestIds(props, "summary-list-item");

  const { title, priceInCents, contrast, last, items } = props;

  const borderColor = contrast ? "rgba(252, 252, 250, 0.10)" : Palette.Gray200;

  return (
    <div css={Css.gap1.pyPx(12).if(!last).add("borderBottom", `1px solid ${borderColor}`).$}>
      {title && (
        <div css={Css.df.w100.aic.jcsb.gap3.$} {...tid.groupTitle}>
          <div>
            <p css={Css.t14.if(!!contrast).white.$}>{title}</p>
          </div>
          <div css={Css.fs1.$} {...tid.groupPriceInCents}>
            <span css={Css.t12.wsnw.if(!!contrast).white.$}>{formatDelta(priceInCents)}</span>
          </div>
        </div>
      )}
      {items.map((item) => {
        const { priceInCents, title } = item;
        return (
          <Fragment key={title}>
            <div key={title} css={Css.df.w100.aic.jcsb.mt1.gap3.$} {...tid}>
              <div {...tid.itemTitle}>
                <p css={Css.t12.gray80.wspl.if(!!contrast).white.$}>{title}</p>
              </div>
              {isDefined(priceInCents) && (
                <div css={Css.fs1.$} {...tid.itemPriceInCents}>
                  <p css={Css.t12.gray80.wsnw.if(!!contrast).white.$}>{formatDeltaPriceDisplay(priceInCents)}</p>
                </div>
              )}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
